import type { StatusIconName } from '../../components/ui/StatusIcon';
import type { WhatsappAlert } from '../../api/types';
import { DnmRequestFailure } from '../../api/dnmFailure';
import { formatIst } from './delayReason';

/**
 * Partner load alert in Suggestions (#289, Contract dnm-work#41). The server decides the state and
 * whether a manual send is allowed (rhino-api R34/R35); this file only maps it to words and tones.
 */
export interface AlertPill {
  bg: string;
  text: string;
  icon: StatusIconName;
  label: string;
}

const PILLS: Record<Exclude<WhatsappAlert['state'], 'NOT_SENT'>, AlertPill> = {
  NOTIFIED: { bg: 'var(--status-green-bg)', text: 'var(--status-green-text)', icon: 'check', label: 'Notified' },
  SENDING: { bg: 'var(--status-amber-bg)', text: 'var(--status-amber-text)', icon: 'hourglass', label: 'Sending' },
  FAILED: { bg: 'var(--status-red-bg)', text: 'var(--status-red-text)', icon: 'close', label: 'Not sent' },
};

/** NOT_SENT has no pill: the row shows only the button. */
export function alertPill(alert: WhatsappAlert): AlertPill | null {
  return alert.state === 'NOT_SENT' ? null : PILLS[alert.state];
}

/** "DD Mon YYYY, HH:mm IST" beside Notified; nothing otherwise. */
export function alertSentAtLabel(alert: WhatsappAlert): string | null {
  return alert.state === 'NOTIFIED' && alert.sentAt ? formatIst(alert.sentAt) : null;
}

export const PARTNER_ALERT_COPY = {
  ALREADY_NOTIFIED: 'Already notified',
  NO_WHATSAPP_NUMBER: 'No WhatsApp number on file.',
  SHIPMENT_NOT_POSTED: "This load isn't open for bids now.",
  NOT_FOUND: "Couldn't find this load or partner. Refresh the page.",
  GENERIC: "Couldn't send. Try again.",
} as const;

const codeOf = (error: unknown) => (error instanceof DnmRequestFailure ? error.code : undefined);

export function isAlreadyNotified(error: unknown): boolean {
  return codeOf(error) === 'ALREADY_NOTIFIED';
}

/** No response came back, so the send may have gone through: Retry reuses the same request id. */
export function isUnconfirmed(error: unknown): boolean {
  return error instanceof DnmRequestFailure && error.response == null;
}

/** Plain words for a refused or failed send; never the code or backend text. */
export function sendFailureCopy(error: unknown): string {
  switch (codeOf(error)) {
    case 'NO_WHATSAPP_NUMBER':
      return PARTNER_ALERT_COPY.NO_WHATSAPP_NUMBER;
    case 'SHIPMENT_NOT_POSTED':
      return PARTNER_ALERT_COPY.SHIPMENT_NOT_POSTED;
    case 'SHIPMENT_NOT_FOUND':
    case 'PARTNER_NOT_FOUND':
      return PARTNER_ALERT_COPY.NOT_FOUND;
    default:
      return PARTNER_ALERT_COPY.GENERIC;
  }
}
